import { Check, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import TrialButton, { type Tier as PaddleTier } from "./TrialButton";

interface Tier {
  name: string;
  paddleTier: PaddleTier;
  price: number;
  annual: number;
  brands: string;
  blurb: string;
  highlighted?: boolean;
}

const tiers: Tier[] = [
  {
    name: "Solo",
    paddleTier: "solo",
    price: 99,
    annual: 990,
    brands: "1 brand",
    blurb: "Solo founders running their own product.",
  },
  {
    name: "Agency",
    paddleTier: "agency",
    price: 249,
    annual: 2490,
    brands: "5 brands",
    blurb: "Freelancers managing client work.",
    highlighted: true,
  },
  {
    name: "Studio",
    paddleTier: "studio",
    price: 599,
    annual: 5990,
    brands: "25 brands",
    blurb: "Small agencies running 10–25 clients.",
  },
];

const sharedFeatures = [
  "Unlimited campaigns",
  "Budget-aware structuring",
  "Works in Claude, ChatGPT & Perplexity",
  "Draft-first publishing — nothing spends until you say go",
  "Geo targeting below country level",
  "Edit live campaigns by chat",
];

const McpPricingCard = () => {
  return (
    <section className="py-20 px-4 bg-background border-b border-border">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Three tiers. Pick yours.
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            7-day free trial on every tier. Monthly billing, cancel any time.
          </p>
        </div>

        <div className="mb-10 rounded-lg border-2 border-primary bg-primary/5 p-5 max-w-3xl mx-auto flex items-center gap-4">
          <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="text-sm">
            <span className="font-semibold text-foreground">Founder pricing:</span>{" "}
            <span className="text-muted-foreground">
              first 50 customers on Solo lock in{" "}
              <span className="text-foreground font-semibold">$49/month for life</span>.
              Use code at checkout.
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`rounded-xl border-2 bg-card text-card-foreground shadow-sm overflow-hidden flex flex-col ${
                tier.highlighted
                  ? "border-primary ring-2 ring-primary/20 shadow-lg"
                  : "border-border"
              }`}
            >
              <div className="p-6 border-b border-border">
                {tier.highlighted && (
                  <Badge className="mb-3 bg-primary/10 text-primary border-0 hover:bg-primary/10">
                    Most popular
                  </Badge>
                )}
                <h3 className="text-xl font-bold text-foreground">{tier.name}</h3>
                <div className="mt-3 flex items-baseline">
                  <span className="text-4xl font-bold text-foreground">${tier.price}</span>
                  <span className="ml-1 text-muted-foreground">/mo</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  or ${tier.annual.toLocaleString("en-US")}/yr — save ~17%
                </p>
                <p className="mt-3 text-sm text-muted-foreground">{tier.blurb}</p>
                <p className="mt-3 text-sm font-semibold text-foreground">
                  {tier.brands}
                </p>
              </div>

              <div className="p-6 flex-grow flex flex-col">
                <ul className="space-y-2 mb-6 flex-grow">
                  {sharedFeatures.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <TrialButton
                  source="pricing_card"
                  tier={tier.paddleTier}
                  variant={tier.highlighted ? "default" : "outline"}
                  className="w-full"
                >
                  Start 7-day free trial
                </TrialButton>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-muted-foreground">
          Need more than 25 brands or white-label?{" "}
          <a
            href="mailto:george@upfixe.com"
            className="text-primary hover:underline"
          >
            Email george@upfixe.com
          </a>
          .
        </p>
      </div>
    </section>
  );
};

export default McpPricingCard;
